import { useEffect, useMemo, useState } from "react";

import type { AxiosInstance } from "axios";
import { useHistory, useLocation } from "react-router-dom";

import { toastError } from "@alpacahq/ui/old";

import { useGetOwnerDetails } from "~/v2/api/hooks";
import {
  useCryptoWalletAPI,
  useGetCryptoAssets,
  useGetCryptoWhitelists,
} from "~/v2/api/hooks/useGetCrypto";
import type { OwnerDetails } from "~/v2/api/rest/account";
import type { CryptoWhitelistedAddress } from "~/v2/api/rest/cryptoWallet";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import type { CryptoSelectOption } from "../../coinTransfers/presentation/CryptoSelect";
import {
  getCryptoAssetDisplayName,
  groupAssetsByChain,
} from "../../coinTransfers/util";
import { STEP_CONFIG } from "../../coinTransfers/whitelistAddress/const";
import type { TravelRuleFormValues } from "../../coinTransfers/whitelistAddress/TravelRuleForm/schema";
import { mapTravelRuleToFormDefaults } from "../../coinTransfers/whitelistAddress/TravelRuleForm/travelRuleDefaults";
import type {
  AssetAddressData,
  AssetAddressDisplay,
  WhitelistAddressMode,
  WhitelistAddressStep,
} from "../../coinTransfers/whitelistAddress/types";

export interface DigitalAssetWhitelistAddressControllerReturn {
  step: WhitelistAddressStep;
  stepConfig: (typeof STEP_CONFIG)[WhitelistAddressStep];
  isEditMode: boolean;
  isLoading: boolean;
  chainOptions: CryptoSelectOption[];
  assetOptionsByChain: Record<string, CryptoSelectOption[]>;
  chainNameByCode: Record<string, string>;
  accountId: string;
  accountDetails?: OwnerDetails;
  cryptoWalletAxiosInstance: AxiosInstance;
  mode: WhitelistAddressMode;
  assetAddressData: AssetAddressData;
  assetAddressDisplay: AssetAddressDisplay;
  travelRuleInitialValues: Partial<TravelRuleFormValues>;
  onAssetAddressNext: (data: AssetAddressData) => void;
  onCancel: () => void;
  onSuccess: () => void;
  onBackToAssetAddress: () => void;
}

export const useDigitalAssetWhitelistAddressController =
  (): DigitalAssetWhitelistAddressControllerReturn => {
    const history = useHistory();
    const location = useLocation();
    const transfersPath = Path.ROUTE_DIGITAL_ASSETS_TRANSFERS;

    const whitelistId = useMemo(() => {
      const params = new URLSearchParams(location.search);
      return params.get("whitelistId") ?? null;
    }, [location.search]);

    const { currentAccount } = useAccountContext({ liveOnly: true });
    const accountId = currentAccount?.id ?? "";

    const { details } = useGetOwnerDetails();
    const cryptoWalletAxiosInstance = useCryptoWalletAPI();

    const isAccountLoading = !currentAccount || !details;
    const isAccountActive = currentAccount?.status === "ACTIVE";

    const isFetchingEnabled = useMemo(
      () => !isAccountLoading && isAccountActive,
      [isAccountLoading, isAccountActive],
    );

    const assets = useGetCryptoAssets({
      accountId,
      options: { enabled: isFetchingEnabled },
    });

    const whitelists = useGetCryptoWhitelists({
      accountId,
      options: { enabled: isFetchingEnabled && !!whitelistId },
    });

    const existingWhitelist = useMemo<CryptoWhitelistedAddress | null>(() => {
      if (!whitelistId || !whitelists.data) {
        return null;
      }
      return whitelists.data.find((w) => w.id === whitelistId) ?? null;
    }, [whitelistId, whitelists.data]);

    const isInvalidEditId =
      !!whitelistId &&
      !whitelists.isLoading &&
      whitelists.isSuccess &&
      !existingWhitelist;

    const mode = useMemo<WhitelistAddressMode>(() => {
      if (whitelistId && existingWhitelist) {
        return { kind: "edit", whitelistId, existing: existingWhitelist };
      }
      return { kind: "create" };
    }, [whitelistId, existingWhitelist]);

    const { chainOptions, assetOptionsByChain, chainNameByCode } = useMemo(
      () => groupAssetsByChain(assets?.data ?? []),
      [assets.data],
    );

    const [createStep, setCreateStep] =
      useState<WhitelistAddressStep>("assetAddress");
    const [createAssetAddressData, setCreateAssetAddressData] =
      useState<AssetAddressData>({
        chain: "",
        asset: "",
        address: "",
      });

    const step: WhitelistAddressStep =
      mode.kind === "edit" ? "travelRule" : createStep;
    const assetAddressData: AssetAddressData =
      mode.kind === "edit"
        ? {
            chain: mode.existing.chain,
            asset: mode.existing.asset,
            address: mode.existing.address,
          }
        : createAssetAddressData;

    const assetAddressDisplay = useMemo(() => {
      const chainOption = chainOptions.find(
        (o) => o.value === assetAddressData.chain,
      );
      const assetOption = assetOptionsByChain[assetAddressData.chain]?.find(
        (o) => o.value === assetAddressData.asset,
      );
      return {
        chainCode: assetAddressData.chain,
        chainLabel: chainOption?.label ?? assetAddressData.chain,
        assetCode: getCryptoAssetDisplayName(
          assetAddressData.chain,
          assetAddressData.asset,
        ),
        assetLabel: assetOption?.label ?? assetAddressData.asset,
      };
    }, [
      assetAddressData.chain,
      assetAddressData.asset,
      chainOptions,
      assetOptionsByChain,
    ]);

    const onAssetAddressNext = (data: AssetAddressData) => {
      setCreateAssetAddressData(data);
      setCreateStep("travelRule");
    };

    const onCancel = () => history.push(transfersPath);
    const onSuccess = () => history.push(transfersPath);

    const travelRuleInitialValues = useMemo<
      Partial<TravelRuleFormValues>
    >(() => {
      if (mode.kind !== "edit" || !mode.existing.travel_rule_info) {
        return {};
      }
      return mapTravelRuleToFormDefaults(mode.existing.travel_rule_info);
    }, [mode]);

    const onBackToAssetAddress = () => {
      if (mode.kind === "edit") {
        onCancel();
        return;
      }
      setCreateStep("assetAddress");
    };

    useEffect(() => {
      if (isAccountLoading) {
        return;
      }
      if (!isAccountActive) {
        history.replace(Path.ROUTE_DASHBOARD);
      }
    }, [history, isAccountActive, isAccountLoading]);

    useEffect(() => {
      if (isInvalidEditId) {
        toastError("Whitelist address not found");
        history.replace(transfersPath);
      }
    }, [isInvalidEditId, history]);

    const isLoading =
      !currentAccount ||
      !details ||
      assets.isLoading ||
      (!!whitelistId && whitelists.isLoading) ||
      isInvalidEditId;

    return {
      step,
      stepConfig: STEP_CONFIG[step],
      isEditMode: mode.kind === "edit",
      isLoading,
      chainOptions,
      assetOptionsByChain,
      chainNameByCode,
      accountId,
      accountDetails: details,
      cryptoWalletAxiosInstance,
      mode,
      assetAddressData,
      assetAddressDisplay,
      travelRuleInitialValues,
      onAssetAddressNext,
      onCancel,
      onSuccess,
      onBackToAssetAddress,
    };
  };
